import { c as _c } from "react/compiler-runtime";
import { FileQuestion } from 'lucide-react';
import type { ComponentProps, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import type { SummarizerSecondaryAction } from '@/components/summarizer/SummarizerActionControls';
import { SummarizerPanel } from '@/components/summarizer/SummarizerPanel';
import { buildSummaryDiagnosticsLines } from '@/components/summarizer/summary-diagnostics';
import { DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import type { ModelOption } from '@/lib/link-preview/summary-options';
import { resolveModelOptionFromCanonical } from '@/lib/link-preview/summary-options';
import { toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import type { TweetAuthor } from '@/types/tweet/tweet';
import type { StreamState } from './summary/types';
import { TweetSummarySettingsDialog } from './TweetSummarySettingsDialog';
type SummaryMode = 'summary' | 'explain' | 'custom';
interface TweetSummarySectionProperties {
  allowAdvancedSummaryOptions: boolean;
  canonicalDraftModel: string;
  closeSettings: () => void;
  draftCustomPrompt: string;
  draftModel: ModelOption;
  draftSummaryLengthIndex: number;
  draftSummaryMode: SummaryMode;
  draftSummarySearchEnabled: boolean;
  explanationState: StreamState;
  isChatEnabled: boolean;
  isExplaining: boolean;
  isSummarizing: boolean;
  liveSearchProviderName: string | null;
  onSummarize?: () => Promise<void> | void;
  onSummaryChat: () => void;
  openSettings: () => void;
  providerHandlesVideo: boolean;
  renderControlsInline?: boolean;
  runActiveMode?: () => Promise<void>;
  setDraftCustomPrompt: (value: string) => void;
  setDraftModel: (value: ModelOption) => void;
  setDraftSummaryLengthIndex: (value: number) => void;
  setDraftSummaryMode: (mode: SummaryMode) => void;
  setDraftSummarySearchEnabled: (value: boolean) => void;
  settingsOpen: boolean;
  shouldRenderSummaryControls: boolean;
  submitSettings: () => Promise<void>;
  summarizeButtonLabel: string;
  summaryMode: SummaryMode;
  summaryState: StreamState;
  supportsSummarySearch: boolean;
  tweetAuthor?: TweetAuthor | null;
}
export function TweetSummarySection(t0) {
  const $ = _c(94);
  const {
    allowAdvancedSummaryOptions,
    shouldRenderSummaryControls,
    summarizeButtonLabel,
    isSummarizing,
    summaryState,
    isChatEnabled,
    onSummaryChat,
    explanationState,
    isExplaining,
    summaryMode,
    runActiveMode,
    onSummarize,
    settingsOpen,
    openSettings,
    closeSettings,
    submitSettings,
    draftSummaryLengthIndex,
    setDraftSummaryLengthIndex,
    draftModel,
    setDraftModel,
    canonicalDraftModel,
    draftCustomPrompt,
    setDraftCustomPrompt,
    draftSummarySearchEnabled,
    setDraftSummarySearchEnabled,
    draftSummaryMode,
    setDraftSummaryMode,
    supportsSummarySearch,
    liveSearchProviderName,
    providerHandlesVideo,
    renderControlsInline: t1
  } = t0;
  const renderControlsInline = t1 === undefined ? false : t1;
  const [isSummaryDismissed, setIsSummaryDismissed] = useState(false);
  let t2;
  if ($[0] !== onSummarize || $[1] !== runActiveMode) {
    t2 = async () => {
      setIsSummaryDismissed(false);
      if (runActiveMode) {
        await runActiveMode();
        return;
      }
      if (onSummarize) {
        await onSummarize();
      }
    };
    $[0] = onSummarize;
    $[1] = runActiveMode;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const triggerPrimaryAction = t2;
  let t3;
  if ($[3] !== submitSettings) {
    t3 = event => {
      event.preventDefault();
      submitSettings().catch(_temp);
    };
    $[3] = submitSettings;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleSettingsSubmit = t3;
  let t4;
  if ($[5] !== closeSettings || $[6] !== openSettings) {
    t4 = open => {
      if (open) {
        openSettings();
      } else {
        closeSettings();
      }
    };
    $[5] = closeSettings;
    $[6] = openSettings;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const handleSettingsOpenChange = t4;
  let t5;
  if ($[8] !== setDraftModel) {
    t5 = canonicalId => {
      const alias = resolveModelOptionFromCanonical(canonicalId);
      if (alias) {
        setDraftModel(alias);
      }
    };
    $[8] = setDraftModel;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleCanonicalModelChange = t5;
  let t6;
  if ($[10] !== onSummaryChat) {
    t6 = event_0 => {
      event_0.preventDefault();
      event_0.stopPropagation();
      onSummaryChat();
    };
    $[10] = onSummaryChat;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const handleDialogChat = t6;
  const isPrimaryActionRunning = summaryMode === "explain" ? isExplaining : isSummarizing;
  let dialogSubmitLabel = "Summarize";
  if (draftSummaryMode === "explain") {
    dialogSubmitLabel = "Explain";
  } else {
    if (draftSummaryMode === "custom") {
      dialogSubmitLabel = "Run custom prompt";
    }
  }
  let t7;
  if ($[12] !== draftCustomPrompt || $[13] !== draftSummaryMode) {
    t7 = draftSummaryMode === "custom" && !toTrimmedNonEmptyString(draftCustomPrompt);
    $[12] = draftCustomPrompt;
    $[13] = draftSummaryMode;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const isDialogSubmitDisabled = t7;
  const summaryDismissedForRender = isSummaryDismissed;
  let t8;
  if ($[15] !== summaryState.text) {
    t8 = toTrimmedNonEmptyString(summaryState.text);
    $[15] = summaryState.text;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const hasSummary = Boolean(t8);
  const shouldShowSummaryCard = !summaryDismissedForRender && (summaryState.status !== "idle" || hasSummary || Boolean(summaryState.error));
  let t9;
  if ($[17] !== summaryDismissedForRender || $[18] !== summaryState.text || $[19] !== triggerPrimaryAction) {
    t9 = event_1 => {
      event_1.preventDefault();
      event_1.stopPropagation();
      if (summaryDismissedForRender && toTrimmedNonEmptyString(summaryState.text)) {
        setIsSummaryDismissed(false);
        return;
      }
      setIsSummaryDismissed(false);
      triggerPrimaryAction().catch(_temp2);
    };
    $[17] = summaryDismissedForRender;
    $[18] = summaryState.text;
    $[19] = triggerPrimaryAction;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const handlePrimaryButtonClick = t9;
  let t10;
  if ($[21] !== isPrimaryActionRunning || $[22] !== openSettings) {
    t10 = event_2 => {
      event_2.preventDefault();
      event_2.stopPropagation();
      if (isPrimaryActionRunning) {
        return;
      }
      openSettings();
    };
    $[21] = isPrimaryActionRunning;
    $[22] = openSettings;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const handleSecondaryMenuSelect = t10;
  let modeLabel = "Summary";
  if (summaryMode === "explain") {
    modeLabel = "Explain";
  } else {
    if (summaryMode === "custom") {
      modeLabel = "Custom prompt";
    }
  }
  const advancedTip = allowAdvancedSummaryOptions ? "Tip \xB7 Adjust model, length, or prompts via the options menu." : null;
  const modelLine = `Model: ${canonicalDraftModel || draftModel}`;
  let t11;
  if ($[24] !== advancedTip || $[25] !== allowAdvancedSummaryOptions || $[26] !== canonicalDraftModel || $[27] !== modeLabel || $[28] !== modelLine || $[29] !== summaryState.metadata) {
    const diagnostics = buildSummaryDiagnosticsLines({
      metadata: summaryState.metadata,
      modeLabel,
      canonicalModelFallback: canonicalDraftModel,
      includeAdvancedTip: advancedTip,
      powerUserEnabled: allowAdvancedSummaryOptions
    });
    t11 = [modelLine, ...diagnostics];
    $[24] = advancedTip;
    $[25] = allowAdvancedSummaryOptions;
    $[26] = canonicalDraftModel;
    $[27] = modeLabel;
    $[28] = modelLine;
    $[29] = summaryState.metadata;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  const summaryHelpContent = t11.join("\n");
  const resolvedPrimaryLabel = summaryDismissedForRender && hasSummary ? "Show summary" : summarizeButtonLabel;
  const resolvedPrimaryDisabled = summaryDismissedForRender && hasSummary ? false : isPrimaryActionRunning;
  const showControls = shouldRenderSummaryControls || summaryDismissedForRender && hasSummary;
  let primarySecondaryAction;
  if (allowAdvancedSummaryOptions && !(summaryDismissedForRender && hasSummary)) {
    let t12;
    if ($[31] !== handleSecondaryMenuSelect || $[32] !== isPrimaryActionRunning) {
      t12 = () => <DropdownMenuContent align="end" className="w-48"><DropdownMenuItem disabled={isPrimaryActionRunning} onSelect={handleSecondaryMenuSelect}>Summarize…</DropdownMenuItem></DropdownMenuContent>;
      $[31] = handleSecondaryMenuSelect;
      $[32] = isPrimaryActionRunning;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== isPrimaryActionRunning || $[35] !== t12) {
      t13 = {
        type: "menu",
        disabled: isPrimaryActionRunning,
        ariaLabel: "Open summarizer options",
        renderMenu: t12
      };
      $[34] = isPrimaryActionRunning;
      $[35] = t12;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    primarySecondaryAction = t13;
  }
  let t12;
  if ($[37] !== handlePrimaryButtonClick || $[38] !== primarySecondaryAction || $[39] !== renderControlsInline || $[40] !== resolvedPrimaryDisabled || $[41] !== resolvedPrimaryLabel || $[42] !== showControls) {
    t12 = !renderControlsInline && showControls ? {
      label: resolvedPrimaryLabel,
      onClick: handlePrimaryButtonClick,
      disabled: resolvedPrimaryDisabled,
      secondaryAction: primarySecondaryAction ?? null
    } : undefined;
    $[37] = handlePrimaryButtonClick;
    $[38] = primarySecondaryAction;
    $[39] = renderControlsInline;
    $[40] = resolvedPrimaryDisabled;
    $[41] = resolvedPrimaryLabel;
    $[42] = showControls;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const panelPrimaryAction = t12;
  let t13;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <p className="mt-2 text-sm text-muted-foreground">Generating explanation…</p>;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let explanationContent = t13;
  if (explanationState.status === "error") {
    const t14 = explanationState.error ?? "Unable to explain tweet.";
    let t15;
    if ($[45] !== t14) {
      t15 = <p className="mt-2 text-sm text-destructive">{t14}</p>;
      $[45] = t14;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    explanationContent = t15;
  } else {
    if (explanationState.text) {
      let t14;
      if ($[47] !== explanationState.text) {
        t14 = <p className="mt-2 leading-relaxed">{explanationState.text}</p>;
        $[47] = explanationState.text;
        $[48] = t14;
      } else {
        t14 = $[48];
      }
      explanationContent = t14;
    }
  }
  const t14 = isChatEnabled ? onSummaryChat : undefined;
  let t15;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setIsSummaryDismissed(true);
    };
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  let t16;
  if ($[50] !== isChatEnabled || $[51] !== summaryHelpContent || $[52] !== summaryState.error || $[53] !== summaryState.status || $[54] !== summaryState.text || $[55] !== t14) {
    t16 = {
      className: "border-primary/30 bg-primary/10 text-muted-foreground",
      status: summaryState.status,
      text: summaryState.text,
      error: summaryState.error,
      markdown: true,
      generatingLabel: "Generating summary\u2026",
      emptyLabel: "Generating summary\u2026",
      onChat: t14,
      isChatEnabled,
      helpContent: summaryHelpContent,
      onDismiss: t15,
      dismissTooltip: "Hide summary",
      textClassName: "leading-relaxed text-muted-foreground"
    };
    $[50] = isChatEnabled;
    $[51] = summaryHelpContent;
    $[52] = summaryState.error;
    $[53] = summaryState.status;
    $[54] = summaryState.text;
    $[55] = t14;
    $[56] = t16;
  } else {
    t16 = $[56];
  }
  let t17;
  if ($[57] !== panelPrimaryAction || $[58] !== shouldShowSummaryCard || $[59] !== t16) {
    t17 = <SummarizerPanel className="mt-3" primaryAction={panelPrimaryAction} cardProps={t16} showCard={shouldShowSummaryCard} />;
    $[57] = panelPrimaryAction;
    $[58] = shouldShowSummaryCard;
    $[59] = t16;
    $[60] = t17;
  } else {
    t17 = $[60];
  }
  let t18;
  if ($[61] !== explanationContent || $[62] !== explanationState.status || $[63] !== explanationState.text || $[64] !== isExplaining) {
    t18 = explanationState.status !== "idle" || explanationState.text.length > 0 ? <div className="mt-3 rounded-lg border border-border/60 bg-card p-3 text-sm text-muted-foreground"><div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground"><FileQuestion className="h-3.5 w-3.5" aria-hidden="true" /><span>Explanation</span>{isExplaining && <CuteSpinner className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}</div>{explanationContent}</div> : null;
    $[61] = explanationContent;
    $[62] = explanationState.status;
    $[63] = explanationState.text;
    $[64] = isExplaining;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  let t19;
  if ($[66] !== allowAdvancedSummaryOptions || $[67] !== canonicalDraftModel || $[68] !== closeSettings || $[69] !== dialogSubmitLabel || $[70] !== draftCustomPrompt || $[71] !== draftSummaryLengthIndex || $[72] !== draftSummaryMode || $[73] !== draftSummarySearchEnabled || $[74] !== handleCanonicalModelChange || $[75] !== handleDialogChat || $[76] !== handleSettingsOpenChange || $[77] !== handleSettingsSubmit || $[78] !== isChatEnabled || $[79] !== isDialogSubmitDisabled || $[80] !== isPrimaryActionRunning || $[81] !== liveSearchProviderName || $[82] !== providerHandlesVideo || $[83] !== setDraftCustomPrompt || $[84] !== setDraftSummaryLengthIndex || $[85] !== setDraftSummaryMode || $[86] !== setDraftSummarySearchEnabled || $[87] !== settingsOpen || $[88] !== supportsSummarySearch) {
    t19 = allowAdvancedSummaryOptions ? <TweetSummarySettingsDialog open={settingsOpen} onOpenChange={handleSettingsOpenChange} isSummarizing={isPrimaryActionRunning} draftSummaryLengthIndex={draftSummaryLengthIndex} onDraftSummaryLengthIndexChange={setDraftSummaryLengthIndex} canonicalDraftModel={canonicalDraftModel} onCanonicalModelChange={handleCanonicalModelChange} draftCustomPrompt={draftCustomPrompt} onDraftCustomPromptChange={setDraftCustomPrompt} draftSummarySearchEnabled={draftSummarySearchEnabled} onDraftSummarySearchEnabledChange={setDraftSummarySearchEnabled} draftSummaryMode={draftSummaryMode} onDraftSummaryModeChange={setDraftSummaryMode} submitLabel={dialogSubmitLabel} isSubmitDisabled={isDialogSubmitDisabled} supportsSummarySearch={supportsSummarySearch} liveSearchProviderName={liveSearchProviderName} providerHandlesVideo={providerHandlesVideo} onSubmit={handleSettingsSubmit} onCancel={closeSettings} onChat={handleDialogChat} isChatEnabled={isChatEnabled} /> : null;
    $[66] = allowAdvancedSummaryOptions;
    $[67] = canonicalDraftModel;
    $[68] = closeSettings;
    $[69] = dialogSubmitLabel;
    $[70] = draftCustomPrompt;
    $[71] = draftSummaryLengthIndex;
    $[72] = draftSummaryMode;
    $[73] = draftSummarySearchEnabled;
    $[74] = handleCanonicalModelChange;
    $[75] = handleDialogChat;
    $[76] = handleSettingsOpenChange;
    $[77] = handleSettingsSubmit;
    $[78] = isChatEnabled;
    $[79] = isDialogSubmitDisabled;
    $[80] = isPrimaryActionRunning;
    $[81] = liveSearchProviderName;
    $[82] = providerHandlesVideo;
    $[83] = setDraftCustomPrompt;
    $[84] = setDraftSummaryLengthIndex;
    $[85] = setDraftSummaryMode;
    $[86] = setDraftSummarySearchEnabled;
    $[87] = settingsOpen;
    $[88] = supportsSummarySearch;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  let t20;
  if ($[90] !== t17 || $[91] !== t18 || $[92] !== t19) {
    t20 = <>{t17}{t18}{t19}</>;
    $[90] = t17;
    $[91] = t18;
    $[92] = t19;
    $[93] = t20;
  } else {
    t20 = $[93];
  }
  return t20;
}
function _temp2() {}
function _temp() {}